import { useEffect, useState } from "react";
import { Copy, KeyRound, FileText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "never");

// Super Admin: create / revoke long-lived keys for the read-only Reporting API (Power BI).
export const ApiKeysCard = () => {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/api-keys").then(({ data }) => setKeys(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/api-keys", { name });
      setFresh(data); setName(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Any Power BI dataset using it will stop refreshing.`)) return;
    try { await api.post(`/admin/api-keys/${k.id}/revoke`); toast.success("Key revoked"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copy = () => { navigator.clipboard.writeText(fresh.api_key); toast.success("Key copied"); };
  const downloadManual = async () => {
    try {
      const { data } = await api.get("/admin/api-reference", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: "Campus Connections - Power BI API Reference.md" });
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download the manual"); }
  };

  return (
    <Card className="max-w-2xl" title="Reporting API (Power BI)">
      <div className="space-y-4" data-testid="api-keys-card">
        <p className="text-sm text-zinc-500">
          Read-only access to every table for Power BI or Excel. Create a key, paste it into the Power BI connection as described in the manual, and revoke it here when it's no longer needed.
        </p>
        <button onClick={downloadManual} className="inline-flex items-center gap-1.5 text-sm text-cc-blue hover:underline" data-testid="api-reference-link">
          <FileText className="w-4 h-4" /> Download the API reference manual (Markdown)
        </button>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name, e.g. Power BI – Campus Relations" data-testid="api-key-name" />
          <Button onClick={create} disabled={busy || name.trim().length < 2} className="bg-cc-blue text-white whitespace-nowrap" data-testid="api-key-create">
            <KeyRound className="w-4 h-4 mr-1" /> Create key
          </Button>
        </div>
        {fresh && (
          <div className="border border-amber-300 bg-amber-50 rounded-md p-3 space-y-2" data-testid="api-key-fresh">
            <p className="text-sm font-medium text-amber-900">Copy this key now — it will not be shown again.</p>
            <div className="flex items-center gap-2">
              <code className="text-xs break-all bg-white border rounded px-2 py-1.5 flex-1" data-testid="api-key-value">{fresh.api_key}</code>
              <Button size="sm" variant="outline" onClick={copy} data-testid="api-key-copy"><Copy className="w-4 h-4" /></Button>
            </div>
            <button className="text-xs text-zinc-500 hover:underline" onClick={() => setFresh(null)} data-testid="api-key-dismiss">I've saved it</button>
          </div>
        )}
        <div className="divide-y border rounded-md" data-testid="api-keys-list">
          {keys.length === 0 && <p className="text-sm text-zinc-400 p-3">No keys yet.</p>}
          {keys.map((k) => (
            <div key={k.id} className="flex items-center justify-between gap-3 p-3 text-sm" data-testid={`api-key-row-${k.id}`}>
              <div className="min-w-0">
                <p className={`font-medium truncate ${k.revoked_at ? "text-zinc-400 line-through" : "text-zinc-800"}`}>{k.name} <span className="font-mono text-xs text-zinc-400 no-underline">{k.prefix}…</span></p>
                <p className="text-xs text-zinc-400">
                  Created {fmt(k.created_at)} by {k.created_by_name || "—"} · Last used {fmt(k.last_used_at)} · {k.request_count || 0} requests
                  {k.revoked_at && <> · <span className="text-red-500">revoked {fmt(k.revoked_at)}</span></>}
                </p>
              </div>
              {!k.revoked_at && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => revoke(k)} data-testid={`api-key-revoke-${k.id}`}>Revoke</Button>}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};
